import { isNil, sumBy } from 'lodash-es';
import { z } from 'zod';
import { type Department, DepartmentSort } from '../../referential/Department';
import { type Region, RegionSort } from '../../referential/Region';
import type { Company } from '../Company/Company';
import {
  hasCompanyEchelon,
  hasDepartmentalEchelon
} from '../ProgrammingPlan/ProgrammingPlanEchelon';
import type { ProgrammingPlanChecked } from '../ProgrammingPlan/ProgrammingPlans';
import { hasPermission, type UserBase, userRegionsForRole } from '../User/User';
import type { UserRole } from '../User/UserRole';
import { LocalPrescriptionComment } from './LocalPrescriptionComment';
import { LocalPrescriptionKey } from './LocalPrescriptionKey';
import {
  LocalPrescriptionSubstanceKindLaboratory,
  SubstanceKindLaboratory
} from './LocalPrescriptionSubstanceKindLaboratory';

export const LocalPrescription = z.object({
  ...LocalPrescriptionKey.shape,
  sampleCount: z.coerce.number(),
  comments: z
    .array(
      LocalPrescriptionComment.pick({
        id: true,
        comment: true,
        createdAt: true,
        createdBy: true
      })
    )
    .nullish(),
  substanceKindsLaboratories: z
    .array(
      LocalPrescriptionSubstanceKindLaboratory.pick({
        laboratoryId: true,
        substanceKind: true
      })
    )
    .nullish(),
  inProgressSampleCount: z.coerce.number().nullish(),
  realizedSampleCount: z.coerce.number().nullish(),
  notAdmissibleSampleCount: z.coerce.number().nullish(),
  compliantSampleCount: z.coerce.number().nullish(),
  nonCompliantSampleCount: z.coerce.number().nullish(),
  previousSampleCount: z.coerce.number().nullish(),
  changedAt: z.coerce.date().nullish(),
  diffusedSampleCount: z.coerce.number().nullish(),
  hasUnappliedChange: z.boolean().nullish()
});

const SlaughterhouseSampleCounts = z
  .array(
    LocalPrescription.pick({
      companySiret: true,
      sampleCount: true
    })
  )
  .min(1, { message: 'Au moins un abattoir doit être renseigné.' });

export const LocalPrescriptionUpdate = z.discriminatedUnion('key', [
  z.object({
    key: z.literal('sampleCount'),
    ...LocalPrescription.pick({
      sampleCount: true
    }).shape
  }),
  z.object({
    key: z.literal('laboratories'),
    substanceKindsLaboratories: z.array(SubstanceKindLaboratory)
  }),
  z.object({
    key: z.literal('slaughterhouseSampleCounts'),
    slaughterhouseSampleCounts: SlaughterhouseSampleCounts
  })
]);

export type LocalPrescription = z.infer<typeof LocalPrescription>;
export type LocalPrescriptionUpdate = z.infer<typeof LocalPrescriptionUpdate>;

export const getCompletionRate = (
  localPrescriptions:
    | Pick<LocalPrescription, 'sampleCount' | 'realizedSampleCount' | 'region'>
    | Pick<
        LocalPrescription,
        'sampleCount' | 'realizedSampleCount' | 'region'
      >[],
  region?: Region | null,
  limitedSentCount: boolean = false
) => {
  const localPrescriptionsWithLimitedSentCount: Pick<
    LocalPrescription,
    'sampleCount' | 'realizedSampleCount' | 'region'
  >[] = (
    Array.isArray(localPrescriptions)
      ? localPrescriptions
      : [localPrescriptions]
  ).map((localPrescription) => ({
    ...localPrescription,
    realizedSampleCount: limitedSentCount
      ? Math.min(
          localPrescription.sampleCount,
          localPrescription.realizedSampleCount ?? 0
        )
      : (localPrescription.realizedSampleCount ?? 0)
  }));

  const totalSampleCount = sumBy(
    localPrescriptionsWithLimitedSentCount.filter((_) =>
      region ? _.region === region : true
    ),
    'sampleCount'
  );

  const totalRealizedSampleCount = sumBy(
    localPrescriptionsWithLimitedSentCount.filter((_) =>
      region ? _.region === region : true
    ),
    'realizedSampleCount'
  );

  return Math.floor(
    totalSampleCount
      ? Number(((totalRealizedSampleCount / totalSampleCount) * 100).toFixed(2))
      : 100
  );
};

export const LocalPrescriptionSort = (
  a: LocalPrescription,
  b: LocalPrescription
) =>
  RegionSort(a.region, b.region) === 0
    ? DepartmentSort(a?.department, b?.department)
    : RegionSort(a.region, b.region);

const LocalPrescriptionPermission = z.enum([
  'updateSampleCount',
  'comment',
  'distributeToDepartments',
  'distributeToSlaughterhouses',
  'updateLaboratories'
]);

export type LocalPrescriptionPermission = z.infer<
  typeof LocalPrescriptionPermission
>;

export const hasLocalPrescriptionPermission = (
  user: Pick<UserBase, 'region' | 'department'>,
  userRole: UserRole,
  programmingPlan: ProgrammingPlanChecked,
  localPrescription: { region: Region; department?: Department | null }
): Record<LocalPrescriptionPermission, boolean> => ({
  updateSampleCount:
    hasPermission(userRole, 'updatePrescription') &&
    userRegionsForRole(user, userRole).includes(localPrescription.region) &&
    programmingPlan.regionalStatus.find(
      (regionStatus) => regionStatus.region === localPrescription.region
    )?.status !== 'Closed',
  distributeToDepartments:
    userRegionsForRole(user, userRole).includes(localPrescription.region) &&
    programmingPlan.regionalStatus.find(
      (regionStatus) => regionStatus.region === localPrescription.region
    )?.status !== 'Closed' &&
    (hasDepartmentalEchelon(programmingPlan.distributionKind)
      ? hasPermission(userRole, 'distributePrescriptionToDepartments')
      : hasPermission(userRole, 'approveProgrammingPlan')),
  distributeToSlaughterhouses:
    hasCompanyEchelon(programmingPlan.distributionKind) &&
    hasPermission(userRole, 'distributePrescriptionToSlaughterhouses') &&
    userRegionsForRole(user, userRole).includes(localPrescription.region) &&
    user.department === localPrescription.department &&
    programmingPlan.departmentalStatus.some(
      (departmentalStatus) =>
        departmentalStatus.region === localPrescription.region &&
        departmentalStatus.department === localPrescription.department &&
        ['Validated', 'SubmittedToDepartments'].includes(
          departmentalStatus.status
        )
    ),
  comment:
    hasPermission(userRole, 'commentPrescription') &&
    userRegionsForRole(user, userRole).includes(localPrescription.region) &&
    programmingPlan.distributionKind === 'REGIONAL' &&
    programmingPlan.regionalStatus.some(
      (regionStatus) =>
        regionStatus.region === localPrescription.region &&
        regionStatus.status === 'SubmittedToRegion'
    ),
  updateLaboratories:
    hasPermission(userRole, 'updatePrescriptionLaboratories') &&
    userRegionsForRole(user, userRole).includes(localPrescription.region) &&
    ((!hasDepartmentalEchelon(programmingPlan.distributionKind) &&
      programmingPlan.regionalStatus.some(
        (regionStatus) =>
          regionStatus.region === localPrescription.region &&
          ['Validated', 'SubmittedToRegion'].includes(regionStatus.status)
      )) ||
      (hasDepartmentalEchelon(programmingPlan.distributionKind) &&
        user.department === localPrescription.department &&
        programmingPlan.departmentalStatus.some(
          (departmentalStatus) =>
            departmentalStatus.region === localPrescription.region &&
            departmentalStatus.department === localPrescription.department &&
            ['Validated', 'SubmittedToDepartments'].includes(
              departmentalStatus.status
            )
        )))
});

export const isLaboratoryAssignmentComplete = (
  substanceKindsLaboratories: LocalPrescription['substanceKindsLaboratories']
): boolean =>
  (substanceKindsLaboratories?.length ?? 0) > 0 &&
  !substanceKindsLaboratories!.some((s) => isNil(s.laboratoryId));

export const filteredLocalPrescriptions = (
  localPrescriptions: LocalPrescription[],
  {
    region,
    department,
    companies,
    regionalPrescriptionIds = []
  }: {
    region?: Region;
    department?: Department;
    companies?: Company[];
    regionalPrescriptionIds?: string[];
  }
) =>
  (localPrescriptions ?? []).filter((_) => {
    if (regionalPrescriptionIds.includes(_.prescriptionId)) {
      return isNil(_.department);
    }
    if (department && companies && companies.length > 0) {
      return (
        _.region === region &&
        _.department === department &&
        companies.some((c) => c.siret === _.companySiret)
      );
    }
    if (department) {
      return (
        _.region === region &&
        _.department === department &&
        isNil(_.companySiret)
      );
    }
    return isNil(_.department);
  });
